import { sumBy } from "lodash-es";

import ChatMessage from "@mapmycustomers/shared/types/chat/ChatMessage";

import { APP_CHAT_THREAD_KEY } from "@app/util/chat/getChatThreadKey";

// Budgets for whole threads, and MAX_STORED_MESSAGES is their total rather than a per-thread cap.
export const MAX_STORED_THREADS = 5;
export const MAX_STORED_MESSAGES = 200;
// The floor a bounded write falls back to, and what V1 stored for the single conversation it knew
// about. Only reached once whole threads have stopped being enough — see sliceChatThreads.
export const MAX_SLICED_MESSAGES = 50;

export interface StoredChatThread {
  conversationId?: number;
  messages: readonly ChatMessage[];
}

// The newest message is the recency, so there is no separate timestamp to keep in step with it.
const getThreadRecency = ({ messages }: StoredChatThread) =>
  messages.reduce((latest, { createdAt }) => (createdAt > latest ? createdAt : latest), "");

const countMessages = (threads: [string, StoredChatThread][]) =>
  sumBy(threads, ([, { messages }]) => messages.length);

// Never slices a record thread: returning to find half a conversation is worse than finding
// none. The newest is kept even when it alone exceeds the budget — chatConversationStorage's quota
// fallback covers the case where it will not fit.
//
// The rep's general thread is held outside the thread budget. It is the one conversation with no
// boundary — it runs for the whole session, while a record thread's arc is the pane — so ranking
// it by recency let a handful of quick questions on records evict the transcript the rep had been
// building all morning.
const retainChatThreads = (
  byThread: Record<string, StoredChatThread>,
  maxThreads: number = MAX_STORED_THREADS,
  maxMessages: number = MAX_STORED_MESSAGES
): Record<string, StoredChatThread> => {
  const withMessages = Object.entries(byThread).filter(([, { messages }]) => messages.length > 0);
  const appThread = withMessages
    .filter(([threadKey]) => threadKey === APP_CHAT_THREAD_KEY)
    .map(([threadKey, thread]): [string, StoredChatThread] => [
      threadKey,
      { ...thread, messages: thread.messages.slice(-maxMessages) },
    ]);
  const ordered = withMessages
    .filter(([threadKey]) => threadKey !== APP_CHAT_THREAD_KEY)
    .sort(([, left], [, right]) => getThreadRecency(right).localeCompare(getThreadRecency(left)));
  const firstDropped = ordered.findIndex(
    (_, index) => index >= maxThreads || countMessages(ordered.slice(0, index + 1)) > maxMessages
  );
  const records =
    firstDropped === -1
      ? ordered
      : ordered.slice(0, Math.min(maxThreads, Math.max(1, firstDropped)));
  return Object.fromEntries([...appThread, ...records]);
};

// The last resort, and the only thing here that breaks the never-slice rule: keeping whole threads
// leaves no smaller write to try once a single thread alone is over quota, so the rep loses every
// thread rather than the oldest part of one. Every attempt above this one still keeps threads whole.
export const sliceChatThreads = (
  byThread: Record<string, StoredChatThread>,
  maxMessages: number = MAX_SLICED_MESSAGES
): Record<string, StoredChatThread> =>
  Object.fromEntries(
    Object.entries(byThread).map(([threadKey, thread]) => [
      threadKey,
      { ...thread, messages: thread.messages.slice(-maxMessages) },
    ])
  );

export default retainChatThreads;
